import React from "react";
import { styled } from "@linaria/react";
import cn from "classnames";
import { color, fonts, lineHeights } from "../../ui";
import { EmptyMark } from "../Table/Td";

export interface JustificationProps {
  showEmptyMark?: boolean;
  minWidth?: string;
  maxWidth?: string;
  width?: string;
  alignment?: "left" | "center" | "right";
}

export interface ColumnMetaData<Data> extends JustificationProps {
  label?: React.ReactNode;
  render?: (data: Data) => React.ReactNode;
  renderFooter?: () => React.ReactNode;
}

interface P<Data, Column extends string> extends React.HTMLAttributes<HTMLTableElement> {
  data: Data[];
  metadata: { [K in Column]: ColumnMetaData<Data> };
  computeRowKey: (data: Data) => string;
}

const THead = styled.thead`
  border-bottom: 1px solid;
  border-bottom-color: ${color.titanium100};
  font: ${fonts.body1};
  background-color: ${color.titanium100};
  height: 32px;
`;
const TBody = styled.tbody`
  tr:not(:first-child) {
    border-top: 1px solid ${color.titanium100};
  }
  font: ${fonts.body1};
`;

const Tr = styled.tr`
  line-height: ${lineHeights[4]};
`;

const Th = styled.th<JustificationProps>`
  padding: 0px 10px;
  color: ${color.carbon800};
  font: ${fonts.body2SemiBold};
  font-weight: 500;

  min-width: ${p => p.minWidth || p.width || "initial"};
  max-width: ${p => p.maxWidth || p.width || "initial"};
  text-align: ${p => p.alignment || "left"};

  div {
    margin-left: ${p => (p.alignment === "left" ? "inherit" : "auto")};
    margin-right: ${p => (p.alignment === "right" ? "inherit" : "auto")};
  }
`;

const Td = styled.td<JustificationProps>`
  padding: 12px;
  font: ${fonts.body1};
  line-height: ${lineHeights[1]};
  color: ${color.carbon900};
  min-width: ${p => p.minWidth || p.width || "initial"};
  max-width: ${p => p.maxWidth || p.width || "initial"};
  text-align: ${p => p.alignment || "left"};

  & > div {
    margin-left: ${p => (p.alignment === "left" ? "inherit" : "auto")};
    margin-right: ${p => (p.alignment === "right" ? "inherit" : "auto")};
  }
`;

const TFooter = styled.tfoot`
  border-top: 1px solid ${color.titanium100};
  font: ${fonts.body1};
  td {
    color: ${color.carbon900};
    font-weight: 500;
  }
`;

const Table = styled.table`
  min-width: 100%;
`;

function MiniTable<Data, Column extends string>({
  className,
  metadata,
  data,
  computeRowKey,
  ...props
}: P<Data, Column>) {
  const columns = Object.keys(metadata) as Column[];
  const hasFooter = Object.values<ColumnMetaData<Data>>(metadata).some(({ renderFooter }) =>
    Boolean(renderFooter),
  );

  return (
    <Table className={className} {...props}>
      <THead>
        <Tr>
          {columns.map(column => {
            const { alignment = "left", label = "", minWidth, maxWidth } = metadata[column];
            return (
              <Th key={column} minWidth={minWidth} maxWidth={maxWidth} alignment={alignment}>
                {label}
              </Th>
            );
          })}
        </Tr>
      </THead>
      <TBody className={cn({ footer: hasFooter })}>
        {!data.length && (
          <Tr>
            {columns.map(column => {
              const { alignment = "left" } = metadata[column];
              return (
                <Td alignment={alignment} key={column}>
                  <EmptyMark />
                </Td>
              );
            })}
          </Tr>
        )}
        {data.map(item => (
          <Tr key={computeRowKey(item)}>
            {columns.map(column => {
              const { alignment = "left", render = () => "", showEmptyMark } = metadata[column];
              const children = render(item);
              return (
                <Td alignment={alignment} key={column}>
                  {children === undefined && showEmptyMark ? <EmptyMark /> : children}
                </Td>
              );
            })}
          </Tr>
        ))}
      </TBody>
      {hasFooter && (
        <TFooter>
          <Tr>
            {columns.map(column => {
              const {
                alignment = "left",
                minWidth,
                maxWidth,
                renderFooter = () => "",
              } = metadata[column];
              return (
                <Td key={column} minWidth={minWidth} maxWidth={maxWidth} alignment={alignment}>
                  {renderFooter()}
                </Td>
              );
            })}
          </Tr>
        </TFooter>
      )}
    </Table>
  );
}

export default MiniTable;
